import { useSessionUser } from "hooks/useSession";
import { useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProjectIdentifiers } from "hooks/project/useProjectIdentifiers";
import { useProjectsApi } from "hooks/useProjects";
import { normalizeId } from "utils";
import {
  Permissions,
  canProject,
  type ProjectPermission,
} from "utils/permissions/permissionConstants";
import { parseProjectPermissions } from "utils/permissions/parseProjectPermissions";
import { parseProjectPermissionsFromState } from "utils/permissions/parseProjectPermissionsFromState";
import {
  getProjectDetailQueryKey,
  readCachedProjectDetail,
} from "hooks/useProjects/projectDetailQuery";

/**
 * Project user type following WorkOS-style pattern
 */
export interface ProjectUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  permissions: string[];
  projectId: string;
  workspaceId: string | null;
  role: string | null;
}

/**
 * Hook result type
 */
export interface UseProjectAuthResult {
  user: ProjectUser | null;
  canProject: (permission: ProjectPermission) => boolean;
  isViewer: boolean;
  embeddedProjectLicense: {
    freeLicensePremiumLocked: boolean;
    isTrialProject: boolean;
    isLicensed: boolean;
    unlicensedMaxDraftScenes?: number;
    createSource?: string;
  };
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Project authorization reads permissions from the canonical GET /projects/:id embed
 * via the same React Query detail cache as useProject (no duplicate fetch).
 */
export const useProjectAuth = (projectId?: string | number | null): UseProjectAuthResult => {
  const queryClient = useQueryClient();
  const { projectId: urlProjectId } = useProjectIdentifiers();
  const targetProjectId = projectId || urlProjectId;
  const normalizedProjectId = targetProjectId ? String(normalizeId(targetProjectId)) : "";
  const hasProjectId = Boolean(normalizedProjectId);

  const { session: profile, isLoading: profileLoading } = useSessionUser();
  const { useProject } = useProjectsApi({ take: 10, order: "DESC" });
  const {
    data: projectState,
    isLoading: projectLoading,
    isError: projectError,
    error: projectErr,
    refetch: projectRefetch,
  } = useProject(hasProjectId ? normalizedProjectId : undefined);

  const projectPayload = useMemo(() => {
    if (!hasProjectId) {
      return null;
    }
    return readCachedProjectDetail(queryClient, normalizedProjectId);
  }, [hasProjectId, normalizedProjectId, queryClient, projectState]);

  const parsedPermissions = useMemo(() => {
    const fromPayload = parseProjectPermissions(projectPayload, normalizedProjectId);
    if (fromPayload) {
      return fromPayload;
    }
    return parseProjectPermissionsFromState(projectState);
  }, [projectPayload, normalizedProjectId, projectState]);

  const user = useMemo<ProjectUser | null>(() => {
    if (!profile || !hasProjectId || !parsedPermissions) {
      return null;
    }

    return {
      id: profile.user_id || "",
      email: profile.email || "",
      firstName: profile.first_name || "",
      lastName: profile.last_name || "",
      permissions: parsedPermissions.permissions,
      projectId: normalizedProjectId,
      workspaceId: parsedPermissions.workspaceId,
      role: parsedPermissions.projectRole ? String(parsedPermissions.projectRole) : null,
    };
  }, [profile, hasProjectId, parsedPermissions, normalizedProjectId]);

  const isLoading = profileLoading || (projectLoading && !projectState && !parsedPermissions);

  const embeddedProjectLicense = useMemo(() => {
    if (!parsedPermissions) {
      return {
        freeLicensePremiumLocked: false,
        isTrialProject: false,
        isLicensed: true,
        unlicensedMaxDraftScenes: undefined,
        createSource: undefined,
      };
    }
    return {
      freeLicensePremiumLocked: parsedPermissions.projectInfo.freeLicensePremiumLocked,
      isTrialProject: parsedPermissions.projectInfo.isTrialProject,
      isLicensed: parsedPermissions.projectInfo.isLicensed,
      unlicensedMaxDraftScenes: parsedPermissions.projectInfo.unlicensedMaxDraftScenes,
      createSource: parsedPermissions.projectInfo.createSource,
    };
  }, [parsedPermissions]);

  const canProjectPermission = useCallback(
    (permission: ProjectPermission): boolean => {
      if (isLoading || !user) {
        return false;
      }
      return canProject(permission, user.permissions);
    },
    [user, isLoading]
  );

  const isViewer = useMemo(() => {
    return !canProjectPermission(Permissions.PROJECT_UPDATE);
  }, [canProjectPermission]);

  const refetch = async () => {
    if (hasProjectId) {
      await projectRefetch();
    }
  };

  return {
    user,
    canProject: canProjectPermission,
    isViewer,
    embeddedProjectLicense,
    isLoading,
    isError: projectError,
    error: (projectErr as Error | null) ?? null,
    refetch,
  };
};

/** Invalidate cached project detail (permissions embed) after role or membership changes. */
export const invalidateProjectAuth = async (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string | number
) => {
  const id = String(normalizeId(projectId));
  await queryClient.invalidateQueries({ queryKey: getProjectDetailQueryKey(id) });
};

export const selectProjectPermissionsFromDetail = (projectPayload: unknown) =>
  parseProjectPermissions(projectPayload);
